import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { EntryFormDialog } from '@/components/common/EntryFormDialog';
import { createInsuranceCompany, updateInsuranceCompany } from '../api/insurance';
import { US_STATES } from '@/lib/constants';
import type { InsuranceCompany, InsuranceCompanyFormData } from '../types';

interface InsuranceCompanyFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company?: InsuranceCompany | null;
  onSuccess: () => void;
}

const initialFormData: InsuranceCompanyFormData = {
  name: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  phone: '',
  email: '',
  isActive: true,
};

export function InsuranceCompanyForm({
  open,
  onOpenChange,
  company,
  onSuccess,
}: InsuranceCompanyFormProps) {
  const [formData, setFormData] = useState<InsuranceCompanyFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isEditMode = !!company?.id;

  useEffect(() => {
    if (company) {
      setFormData({
        name: company.name,
        address1: company.address1 || '',
        address2: company.address2 || '',
        city: company.city || '',
        state: company.state || '',
        zip: company.zip || '',
        phone: company.phone || '',
        email: company.email || '',
        isActive: company.isActive,
      });
    } else {
      setFormData(initialFormData);
    }
    setError('');
  }, [company, open]);

  const handleChange = (field: keyof InsuranceCompanyFormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.name.trim()) {
      setError('Company name is required');
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditMode && company) {
        await updateInsuranceCompany(company.id, formData);
      } else {
        await createInsuranceCompany(formData);
      }
      onSuccess();
      onOpenChange(false);
    } catch (err) {
      const errorObj = err as { response?: { data?: { message?: string } } };
      setError(errorObj.response?.data?.message || 'Failed to save company');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <EntryFormDialog
      isOpen={open}
      onClose={() => onOpenChange(false)}
      title={isEditMode ? 'Edit Insurance Company' : 'Add Insurance Company'}
      error={error}
      isSubmitting={isSubmitting}
      submitLabel="Add Company"
      editLabel="Save Changes"
      isEditing={isEditMode}
      onSubmit={handleSubmit}
      maxWidth="max-w-lg"
    >
      <div className="space-y-2">
        <Label htmlFor="name">Company Name *</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          placeholder="Enter company name"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="address1">Address Line 1</Label>
        <Input
          id="address1"
          value={formData.address1 || ''}
          onChange={(e) => handleChange('address1', e.target.value)}
          placeholder="Street address"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="address2">Address Line 2</Label>
        <Input
          id="address2"
          value={formData.address2 || ''}
          onChange={(e) => handleChange('address2', e.target.value)}
          placeholder="Apt, suite, etc."
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="city">City</Label>
          <Input
            id="city"
            value={formData.city || ''}
            onChange={(e) => handleChange('city', e.target.value)}
            placeholder="City"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="state">State</Label>
          <Select
            id="state"
            value={formData.state || ''}
            onChange={(e) => handleChange('state', e.target.value)}
          >
            <option value="">Select</option>
            {US_STATES.map((state) => (
              <option key={state.code} value={state.code}>
                {state.code}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="zip">ZIP</Label>
          <Input
            id="zip"
            value={formData.zip || ''}
            onChange={(e) => handleChange('zip', e.target.value)}
            placeholder="ZIP"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="phone">Phone</Label>
          <Input
            id="phone"
            type="tel"
            value={formData.phone || ''}
            onChange={(e) => handleChange('phone', e.target.value)}
            placeholder="(555) 123-4567"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={formData.email || ''}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="email@company.com"
          />
        </div>
      </div>

      {isEditMode && (
        <div className="flex items-center space-x-2">
          <Checkbox
            id="isActive"
            checked={formData.isActive}
            onChange={(e) => handleChange('isActive', e.target.checked)}
          />
          <Label htmlFor="isActive" className="text-sm font-normal">
            Active
          </Label>
        </div>
      )}
    </EntryFormDialog>
  );
}
